import { useEffect, useRef } from 'react'

import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { checkCanAccessDiscoverMoreUseCase } from '@webapp/core/business-logic/use-cases/CheckCanAccessDiscoverMore'
import { makeSelectFeatureSwitches } from '@webapp/core/stores/global'
import { useAppRoutes, useFeatureFlag } from '@webapp/hooks'
import { DISCOVER_MORE_EVENTS } from '@webapp/platform/discover-more/constants'
import { DiscoverMorePageTabs } from '@webapp/platform/discover-more/pages'
import {
  AP_PO_EXPORT_PROMO_EXPERIMENT,
  AP_PO_EXPORT_PROMO_FEATURE,
  AP_PO_EXPORT_PROMO_MIN_RECORD_COUNT,
  AP_PO_EXPORT_PROMO_SURFACE,
} from '../../constants'
import { useApPoExportPromoDismissal } from '../../models/useApPoExportPromoDismissal'

const trackApPoExportPromoEvent = (
  event: (typeof DISCOVER_MORE_EVENTS)[keyof typeof DISCOVER_MORE_EVENTS],
  recordCount: number
) => {
  window.analytics?.track(event, {
    feature: AP_PO_EXPORT_PROMO_FEATURE,
    surface: AP_PO_EXPORT_PROMO_SURFACE,
    experiment: AP_PO_EXPORT_PROMO_EXPERIMENT,
    record_count: recordCount,
  })
}

interface UseApPoExportPromoPresenterParams {
  recordCount: number
  onNavigate?: (path: string) => void
}

export const useApPoExportPromoPresenter = ({
  recordCount,
  onNavigate,
}: UseApPoExportPromoPresenterParams) => {
  const history = useHistory()
  const { url } = useAppRoutes()
  const PAY_4551_DISCOVER_MORE_PAGE = useFeatureFlag(
    'PAY_4551_DISCOVER_MORE_PAGE'
  )
  const { disable_ap_navigation: disableApNavigation } = useSelector(
    makeSelectFeatureSwitches()
  )
  const { isDismissed, dismiss } = useApPoExportPromoDismissal()

  // Learn more targets a flag-protected route, so the flag gates the promo too.
  const isEligible =
    disableApNavigation === true &&
    checkCanAccessDiscoverMoreUseCase({ PAY_4551_DISCOVER_MORE_PAGE }) &&
    recordCount >= AP_PO_EXPORT_PROMO_MIN_RECORD_COUNT &&
    !isDismissed

  const hasTrackedView = useRef(false)

  useEffect(() => {
    if (!isEligible || hasTrackedView.current) return

    hasTrackedView.current = true
    trackApPoExportPromoEvent(DISCOVER_MORE_EVENTS.PROMO_VIEWED, recordCount)
  }, [isEligible, recordCount])

  const onLearnMoreClick = () => {
    trackApPoExportPromoEvent(DISCOVER_MORE_EVENTS.PROMO_CLICKED, recordCount)
    const path = url('DiscoverMorePage', { tab: DiscoverMorePageTabs.AP })

    if (onNavigate) {
      onNavigate(path)
    } else {
      history.push(path)
    }
  }

  const onLaterClick = () => {
    trackApPoExportPromoEvent(DISCOVER_MORE_EVENTS.PROMO_DISMISSED, recordCount)
    dismiss()
  }

  return {
    states: { isEligible },
    commands: { onLearnMoreClick, onLaterClick },
  }
}
